/**
 * The Satatam Help drawer.
 *
 * Answers arrive as plain text and are rendered as plain text -- paragraphs
 * and `- ` bullets, never markup. The server allowlists the links it returns,
 * so the only judgement made here is internal (client-side navigation, which
 * closes the drawer) versus external (a new tab, `noopener`).
 *
 * The focus trap, the `inert` sweep, Escape and the focus restore are the same
 * ones `InboxShortcutHelp` uses. They are copied rather than imported: that
 * component owns a different dialog with a different body, and a shared hook
 * would have to be introduced to both at once, which is not this task.
 */
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router";

import { useHelp, type HelpMessage } from "../shared/help-context.tsx";

const QUESTION_MAX = 2000;
const CONTACT_HREF = "https://satatam.co/contact.html?topic=general";

/** Plain text → paragraphs and "- " bullet lists. Never HTML. */
export function renderAnswer(text: string) {
  const blocks = text.replace(/\r\n/g, "\n").split(/\n\s*\n/).map((b) => b.trim()).filter(Boolean);
  return blocks.map((block, i) => {
    const lines = block.split("\n");
    if (lines.every((l) => l.startsWith("- "))) {
      return <ul key={i}>{lines.map((l, j) => <li key={j}>{l.slice(2)}</li>)}</ul>;
    }
    return <p key={i}>{block}</p>;
  });
}

function Bubble({ m, onNavigate, onRetry }: { m: HelpMessage; onNavigate: () => void; onRetry: (q: string) => void }) {
  return (
    <div className={`help-bubble help-bubble--${m.role}`} data-help-role={m.role}>
      <div className="help-bubble__text">{m.role === "assistant" ? renderAnswer(m.text) : <p>{m.text}</p>}</div>
      {m.links && m.links.length > 0 && (
        <div className="help-bubble__links">
          {m.links.map((l) => (l.href.startsWith("/")
            ? <Link key={l.href + l.label} to={l.href} className="btn btn-ghost help-link" onClick={onNavigate}>{l.label} →</Link>
            : <a key={l.href + l.label} href={l.href} target="_blank" rel="noopener noreferrer" className="btn btn-ghost help-link">{l.label} ↗</a>
          ))}
        </div>
      )}
      {m.retryOf && (
        <button type="button" className="btn help-retry" data-help-retry onClick={() => onRetry(m.retryOf!)}>Try again</button>
      )}
    </div>
  );
}

export function HelpDrawer() {
  const { open, close, messages, ask, busy } = useHelp();
  const [draft, setDraft] = useState("");
  const closeRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const backdropRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    previousFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    const backdrop = backdropRef.current;
    const inertStates = [...document.body.children]
      .filter((element): element is HTMLElement => (
        element instanceof HTMLElement && element !== backdrop
      ))
      .map((element) => [element, element.inert] as const);
    for (const [element] of inertStates) element.inert = true;
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close();
        return;
      }
      if (event.key !== "Tab") return;
      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusable = [...dialog.querySelectorAll<HTMLElement>(
        "button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])",
      )].filter((element) => !element.hidden && element.getClientRects().length > 0);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (
        !dialog.contains(active)
        || (!event.shiftKey && active === last)
        || (event.shiftKey && active === first)
      ) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      for (const [element, wasInert] of inertStates) element.inert = wasInert;
      const opener = previousFocusRef.current;
      if (opener?.isConnected) opener.focus({ preventScroll: true });
    };
  }, [close, open]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages, busy, open]);

  if (!open) return null;
  const overLimit = draft.length > QUESTION_MAX;
  const send = () => {
    const q = draft;
    if (!q.trim() || overLimit || busy) return;
    setDraft("");
    void ask(q);
  };
  return createPortal(
    <div
      ref={backdropRef}
      className="help-backdrop"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        close();
      }}
    >
      {/* "Satatam" is deliberate here and NOT `useWhiteLabelChrome().productLabel`
          (white-label-chrome.tsx:107-110): this drawer is Satatam's own support
          desk, not the tenant's console chrome -- its answers and its contact
          link both go to Satatam, so naming it after the tenant would misdirect. */}
      <section ref={dialogRef} className="panel help-drawer" role="dialog" tabIndex={-1} aria-modal="true" aria-label="Satatam Help">
        <header className="help-drawer__head">
          <div>
            <span className="app-eyebrow">Support</span>
            <h2>Satatam Help</h2>
          </div>
          <button ref={closeRef} type="button" className="btn" onClick={close} aria-label="Close Satatam Help">Close</button>
        </header>
        <div ref={listRef} className="help-drawer__list" aria-live="polite">
          {messages.length === 0 && (
            <p className="help-drawer__empty">Ask anything about setting up or using the Satatam operator app.</p>
          )}
          {messages.map((m) => <Bubble key={m.id} m={m} onNavigate={close} onRetry={(q) => void ask(q)} />)}
          {busy && <div className="help-bubble help-bubble--assistant help-bubble--thinking" data-help-thinking><p>Thinking…</p></div>}
        </div>
        <form className="help-drawer__composer" onSubmit={(e) => { e.preventDefault(); send(); }}>
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={2}
            placeholder="How do I…"
            aria-label="Your question"
            disabled={busy}
          />
          <div className="help-drawer__composer-row">
            <span className={`help-drawer__count${overLimit ? " is-over" : ""}`} aria-live="polite">
              {draft.length > 1800 ? `${draft.length}/${QUESTION_MAX}` : ""}
            </span>
            <button type="submit" className="btn btn-primary" data-help-send disabled={busy || overLimit || !draft.trim()}>Send</button>
          </div>
        </form>
        <footer className="help-drawer__foot">
          Need a person? <a href={CONTACT_HREF} target="_blank" rel="noopener noreferrer">Talk to Satatam</a>
        </footer>
      </section>
    </div>,
    document.body,
  );
}
